import React from 'react';
import { ShieldCheck, Scale, Award, ArrowRight, MessageCircle, CheckCircle2, Key, Building2, FileCheck } from 'lucide-react';
import { useProperties } from '../context/PropertyContext';
import { PropertyCard } from '../components/PropertyCard';
import { useSEO } from '../hooks/useSEO';
import { getBaseOrganizationSchema } from '../utils/seoManager';

export const HomePage = ({ onNavigate, onSelectProperty }) => {
  const { properties, getWhatsAppLink } = useProperties();

  // Dynamic SEO Configuration
  useSEO({
    title: 'EM Asesoría Inmobiliaria y Jurídica | Lic. Edgar Molina | Parral, Chihuahua',
    description: 'Venta, renta y asesoría legal inmobiliaria en Hidalgo del Parral, Chihuahua. Trámites de escrituración, juicios sucesorios, avalúos y créditos Infonavit bajo la dirección del Lic. Edgar Molina.',
    keywords: 'casas en venta en Parral Chihuahua, inmobiliarias en Parral Chihuahua para vender casa, vender mi casa en Parral, renta de casas en Parral, terrenos en venta Parral Chihuahua, asesoria legal inmobiliaria en Parral, abogado inmobiliario Parral Chihuahua, tramites de escrituracion en Parral, juicios sucesorios para propiedades Parral, Lic Edgar Molina asesor juridico e inmobiliario Parral, EM Asesoria Inmobiliaria y Juridica Parral, despacho juridico inmobiliario Parral',
    canonicalUrl: 'https://asesoriasem.com/',
    ogTitle: 'EM Asesoría Inmobiliaria y Jurídica | Lic. Edgar Molina | Parral, Chihuahua',
    ogDescription: 'Compra, vende y renta propiedades con certeza legal en Hidalgo del Parral, Chihuahua. Asesoría jurídica, escrituras y créditos hipotecarios.',
    ogUrl: 'https://asesoriasem.com/',
    structuredData: getBaseOrganizationSchema()
  });

  // 3 propiedades aleatorias destacadas
  const featuredProperties = React.useMemo(() => {
    const available = properties.filter((p) => {
      const s = (p.status || '').toLowerCase().trim();
      return s === 'disponible' || s === 'activa';
    });
    const shuffled = [...available];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 3);
  }, [properties]);

  return (
    <div className="home-page fade-in">
      {/* HERO SECTION */}
      <section
        className="hero-section"
        style={{
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-white)',
          paddingTop: 'var(--spacing-4xl)',
          paddingBottom: 'var(--spacing-4xl)',
          position: 'relative',
          backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(243, 207, 104, 0.12) 0%, transparent 50%)'
        }}
      >
        <div className="container" style={{ textAlign: 'center', maxWidth: '960px' }}>
          <div className="section-tag dark" style={{ margin: '0 auto var(--spacing-md) auto' }}>
            <span>Certeza Jurídica & Asesoría Inmobiliaria Integral</span>
          </div>

          <h1 style={{ color: 'var(--color-white)', marginBottom: 'var(--spacing-md)', lineHeight: 1.15 }}>
            Bienes Raíces con <span style={{ color: 'var(--color-accent)' }}>Respaldo Legal Garantizado</span> en Parral
          </h1>

          <p
            className="lead"
            style={{
              color: 'rgba(255, 255, 255, 0.88)',
              maxWidth: '780px',
              margin: '0 auto var(--spacing-xl) auto',
              fontSize: '1.18rem'
            }}
          >
            Compra, vende y renta inmuebles en Hidalgo del Parral, Chihuahua con la asesoría y representación notarial del <strong>Licenciado Edgar Molina</strong>.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-accent btn-lg"
              onClick={() => onNavigate('properties')}
            >
              <span>Explorar Catálogo</span>
            </button>

            <button
              type="button"
              className="btn btn-outline-white btn-lg"
              onClick={() => onNavigate('sell')}
            >
              <span>Vender o Rentar mi Propiedad</span>
            </button>

            <a
              href={getWhatsAppLink('Hola Lic. Edgar Molina, deseo información para comprar, vender o tramitar un inmueble en Parral.')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-white btn-lg"
            >
              <MessageCircle size={18} />
              <span>WhatsApp: 627 279 2964</span>
            </a>
          </div>
        </div>
      </section>

      {/* 4 CORE VALUE PILLARS */}
      <section className="section" style={{ backgroundColor: 'var(--color-surface-subtle)', padding: 'var(--spacing-3xl) 0' }}>
        <div className="container">
          <div className="section-header" style={{ marginBottom: 'var(--spacing-2xl)' }}>
            <div className="section-tag">
              <span>Pilares de Servicio</span>
            </div>
            <h2 className="section-title">Solución Integral Inmobiliaria y Legal</h2>
            <p className="section-subtitle">
              Combinamos la comercialización estratégica de inmuebles con la certeza de un despacho jurídico especializado en Parral.
            </p>
          </div>

          <div className="grid grid-4" style={{ gap: '20px' }}>
            <div className="card" style={{ padding: 'var(--spacing-xl)', borderTop: '4px solid var(--color-primary)' }}>
              <Building2 size={28} color="var(--color-primary)" style={{ marginBottom: '14px' }} />
              <h3 style={{ fontSize: '1.15rem', marginBottom: '8px' }}>Captación y Venta</h3>
              <p style={{ fontSize: '0.88rem', color: 'var(--color-text-muted)', lineHeight: '1.6' }}>
                Promoción profesional de casas, terrenos y locales con opinión de valor y filtrado de compradores en Parral.
              </p>
            </div>

            <div className="card" style={{ padding: 'var(--spacing-xl)', borderTop: '4px solid var(--color-primary)' }}>
              <ShieldCheck size={28} color="var(--color-primary)" style={{ marginBottom: '14px' }} />
              <h3 style={{ fontSize: '1.15rem', marginBottom: '8px' }}>Arrendamiento Seguro</h3>
              <p style={{ fontSize: '0.88rem', color: 'var(--color-text-muted)', lineHeight: '1.6' }}>
                Contratos blindados con protección por Ley de Extinción de Dominio y revisión crediticia de arrendatarios.
              </p>
            </div>

            <div className="card" style={{ padding: 'var(--spacing-xl)', borderTop: '4px solid var(--color-primary)' }}>
              <Scale size={28} color="var(--color-primary)" style={{ marginBottom: '14px' }} />
              <h3 style={{ fontSize: '1.15rem', marginBottom: '8px' }}>Trámites Notariales</h3>
              <p style={{ fontSize: '0.88rem', color: 'var(--color-text-muted)', lineHeight: '1.6' }}>
                Escrituración, cancelación de gravámenes, cálculo y exención legal de ISR e ISAI ante Notaría.
              </p>
            </div>

            <div className="card" style={{ padding: 'var(--spacing-xl)', borderTop: '4px solid var(--color-primary)' }}>
              <FileCheck size={28} color="var(--color-primary)" style={{ marginBottom: '14px' }} />
              <h3 style={{ fontSize: '1.15rem', marginBottom: '8px' }}>Juicios y Regularización</h3>
              <p style={{ fontSize: '0.88rem', color: 'var(--color-text-muted)', lineHeight: '1.6' }}>
                Juicios sucesorios intestamentarios, usucapión y asesoría en créditos Infonavit, FOVISSSTE y Bancarios.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">
              <span>Oportunidades Destacadas</span>
            </div>
            <h2 className="section-title">Casas y Terrenos en Venta y Renta en Parral</h2>
            <p className="section-subtitle">
              Inmuebles verificados jurídica y físicamente en Hidalgo del Parral, Chihuahua.
            </p>
          </div>

          {featuredProperties.length > 0 ? (
            <div className="grid grid-3">
              {featuredProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onSelectProperty={onSelectProperty}
                />
              ))}
            </div>
          ) : (
            <div className="card" style={{ padding: 'var(--spacing-3xl)', textAlign: 'center' }}>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-lg)' }}>
                Explora el catálogo completo para conocer todas las propiedades disponibles en la región.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={() => onNavigate('properties')}
              >
                <span>Ver Catálogo de Inmuebles</span>
                <ArrowRight size={18} />
              </button>
            </div>
          )}

          {featuredProperties.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: 'var(--spacing-2xl)' }}>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={() => onNavigate('properties')}
              >
                <span>Ver Catálogo Completo de Inmuebles</span>
                <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* OWNER ACQUISITION SECTION */}
      <section
        className="section section-bg-dark"
        style={{
          backgroundImage: 'linear-gradient(135deg, #001730 0%, #00244a 100%)',
          borderTop: '3px solid var(--color-accent)'
        }}
      >
        <div className="container">
          <div className="owner-hero-grid">
            <div>
              <div className="section-tag dark" style={{ marginBottom: 'var(--spacing-sm)' }}>
                <span>Para Propietarios en Parral</span>
              </div>
              <h2 style={{ color: 'var(--color-white)', marginBottom: 'var(--spacing-md)' }}>
                ¿Deseas Vender o Rentar tu Propiedad con Cero Riesgos?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', lineHeight: '1.7', marginBottom: 'var(--spacing-lg)' }}>
                Ponemos a tu disposición marketing inmobiliario especializado, avalúos certificados, calificación rigurosa de prospectos y todo el acompañamiento notarial y jurídico del <strong>Licenciado Edgar Molina</strong>.
              </p>

              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: 'var(--spacing-xl)', padding: 0 }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--color-white)' }}>
                  <CheckCircle2 size={18} color="var(--color-accent)" style={{ flexShrink: 0 }} />
                  <span>Opinión de valor y avalúo preliminar sin costo ni compromiso</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--color-white)' }}>
                  <CheckCircle2 size={18} color="var(--color-accent)" style={{ flexShrink: 0 }} />
                  <span>Validación de libertad de gravamen en el Registro Público</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--color-white)' }}>
                  <CheckCircle2 size={18} color="var(--color-accent)" style={{ flexShrink: 0 }} />
                  <span>Cierre notarial seguro y cálculo de exención del ISR</span>
                </li>
              </ul>

              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-accent btn-lg"
                  onClick={() => onNavigate('sell')}
                >
                  <span>Registrar Propiedad para Venta o Renta</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>

            {/* Visual Attention Box */}
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '2px solid var(--color-accent)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--spacing-2xl)',
                boxShadow: '0 16px 40px rgba(0, 0, 0, 0.25)',
                textAlign: 'center'
              }}
            >
              <img
                src="/EM Asesorias.svg"
                alt="EM Asesoría Inmobiliaria y Jurídica - Lic. Edgar Molina"
                width="90"
                height="90"
                style={{ height: '90px', width: '90px', margin: '0 auto var(--spacing-md) auto' }}
              />
              <h3 style={{ color: 'var(--color-primary)', marginBottom: '8px' }}>Atención Personalizada</h3>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.92rem', marginBottom: 'var(--spacing-lg)', lineHeight: '1.6' }}>
                Comunícate directamente con el <strong>Licenciado Edgar Molina</strong> para resolver dudas sobre escrituración, herencias, créditos o regularización de predios en Parral.
              </p>
              <a
                href={getWhatsAppLink('Hola Lic. Edgar Molina, quiero poner mi propiedad en venta o renta con EM Asesoría.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp btn-full"
              >
                <MessageCircle size={20} />
                <span>WhatsApp: 627 279 2964</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="section">
        <div className="container">
          <div className="card" style={{ padding: 'var(--spacing-2xl)', borderLeft: '4px solid var(--color-accent)' }}>
            <h3 style={{ marginBottom: '12px', color: 'var(--color-primary)' }}>
              ¿Tienes dudas sobre escrituras, créditos Infonavit o juicios sucesorios en Parral?
            </h3>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-lg)', lineHeight: '1.6' }}>
              Consulta nuestra sección de Preguntas Frecuentes con respuestas jurídicas sobre requisitos para comprar casa, cálculo de impuestos ISAI/ISR y regularización de propiedades en Chihuahua.
            </p>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => onNavigate('guide')}
            >
              <span>Consultar Preguntas Frecuentes</span>
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
